// BUILDING TYPES — what a venue the town voted for is built FROM (Khari,
// 2026-10-30: *"We will build models here, and add them to the code. then the
// pipeline will duplicate them for purposes like this. some will be bespoke,
// others will be general and made for duplication, like this underground club.
// The difference would be the media that plays on the walls … the media of
// whatever artist is currently hosting the room. There is a DJ booth there."*).
//
// The split (API docs/VENUE_PIPELINE_PLAN.md): a TYPE lives here, in code — the
// model, where its screens hang, where the booth is, how dark it runs, whether
// it may be duplicated. An INSTANCE lives in the database — the name the town
// voted for, its look, its hours, its host, where it stands — and reaches the
// World through GET /api/world/venues (city/venueRegistry). Keyed EXACTLY like
// the API's `type_key`, so the server can propose a type without knowing
// anything about models.
//
// An instance's room is `{ ...ROOMS[type.room], …its own name and look }`, so
// two clubs of one type are two room ids pointing at one GLB: useGLTF caches by URL,
// the model is downloaded once however many of them stand in the city.
//
// ── COORDINATES ─────────────────────────────────────────────────────────────
// Everything below is in ROOM space, the same space as rooms.js `spawn`: world
// units, centred on the model's footprint, floor at y=0, BEFORE the venue
// tool's placement scale. Room space depends on the fit, so each type records
// the `fitTo` it was measured at (`screensFitTo`); MeshInterior rescales when a
// saved row or ?venuefit fits the room differently.

import { faceMarks } from './city/facing';
export const VENUE_TYPES = {
  // THE GANGSTAFUEL GYM, bespoke (2026-10-31, Khari: "the 'GangstaFuel' Gym …
  // black and grey with neon lime green as the highlight … a fitness instructor
  // … a drink is available"). Built once: ROOMS.gym (scripts/make_gym.mjs), which
  // carries the machines, the fuel bar and the logo walls. API city/venueTypes 'gym'.
  gym: { room: 'gym', duplicable: false, light: 0.55 },
  // HYPETUNES HEADQUARTERS, bespoke (2026-10-31: "a helpdesk. Place Lyra at the
  // front desk."). ROOMS.hq (scripts/make_hq.mjs). API city/venueTypes 'headquarters'.
  // On an empty lot behind the spawn (Khari: "an empty lot behind the spawn with
  // a generated building"), so it brings its own building: LotBuildings raises
  // it from `lot` — three storeys of lit glass over a warm lobby, the HypeTunes
  // amber as its trim line and the logo lit on the roof.
  headquarters: {
    room: 'hq', duplicable: false,
    // SITED (2026-10-31, Khari: "an inside room is still able to view the live
    // outside … I would like to do that in the hypetunes building and in the
    // coffee shop"): the room stands in its building and the glass front shows
    // the real street — the arrival, the city — like the Corner Market's.
    sited: true,
    lot: { h: 6.4, body: '#1b1d22', glass: '#1f2a33', trim: '#ffb25e', logo: '/logo.png' },
  },
  // THE HYPEFLYER PRINT SHOP, bespoke (2026-10-31, Khari: "change the laundromat
  // into a flyer and graphics shop"). ROOMS.printshop (scripts/make_printshop.mjs).
  // API city/venueTypes 'print-shop'. It stands ON the old laundromat
  // (`LM_Laundrette`, migration 462), so it is SITED: the room stands in that
  // building, the street live through its windows, the laundromat stepping aside
  // while you are inside — and from outside its facade is repainted
  // (CityModel `FACADES`: the sign and the awning say what it is now).
  'print-shop': { room: 'printshop', duplicable: false, sited: true, light: 0.8 },
  // HYPE-U, THE HYPETUNES UNIVERSITY, bespoke (2026-11-01, Khari: "a new venue
  // called Hype-U. it will be our learning university"). ROOMS.hypeu, the lobby
  // (scripts/make_hypeu.mjs); its elevator rides to a classroom per course
  // (city/hypeu, ROOMS.hypeu_class). API city/venueTypes 'university'.
  // SITED (2026-11-01, Khari, at the lobby's glass doors: "We will place a real
  // view outside instead of this image"): the lobby stands in its building and
  // the doors look onto the real street, like HQ's front; the painted daylight
  // behind them (ROOMS.hypeu `sitedHide`) only shows in the virtual room
  // (?enter=hypeu). On an empty lot it raises a navy-and-gold building of its
  // own (LotBuildings). The classrooms upstairs stay virtual rooms.
  university: {
    room: 'hypeu', duplicable: false, sited: true, light: 1.05,
    lot: { h: 6.8, body: '#1d2b53', glass: '#26365e', trim: '#d8a640' },
  },
  // THE UNDERGROUND CLUB, made from the Warehouse (ROOMS.warehouse): a small
  // concrete room with a DJ booth in an alcove in the back wall, a speaker stack
  // either side of it, a mezzanine bar along +x. Made for duplication — the room
  // is the same everywhere, what plays on its walls is the host's.
  'underground-club': {
    room: 'warehouse',
    duplicable: true,
    // Dark. Multiplies MeshInterior's whole rig (hemisphere, ambient and the
    // ceiling practicals together), so this is one low light, not a new model.
    // The screens are self-lit and carry the room.
    light: 0.3,
    // MEASURED off public/models/venues/warehouse.glb (2026-10-30), not guessed,
    // and converted the way MeshInterior converts: its box is the union of each
    // mesh's bounding box pushed through its matrix, which on this model's
    // tilted nodes reads x 27.39..34.51, z −7.83..12.25 (metres) — wider than the
    // vertices (27.84) by half a metre, and it is THAT box the room is centred
    // on. So at fitTo 11: scale 11 / 20.086 = 0.54765, room = (model − (30.949,
    // 2.209)) × 0.54765, floor detected at 0. Re-measure with a wall map if the
    // GLB is ever rebuilt — these follow the geometry, not the other way round.
    screensFitTo: 11,
    screens: [
      // The `aguia` picture plane on the +x wall, under the mezzanine: model
      // x 34.50, z 2.63..6.59, y 0..3.12, facing −x. The screen sits exactly
      // over it, 2cm proud, so the host's media REPLACES the old poster rather
      // than hanging beside it. Its top 0.3m runs behind the mezzanine slab, as
      // the poster's does — the slab is what frames it.
      { pos: [1.9337, 0.8543, 1.3149], size: [2.1687, 1.7087], rotY: -Math.PI / 2 },
      // Above the booth, on the back wall (model z −1.03, facing +z). The wall
      // there is clear from x 29.8 to 34.2 and y 3.2 to the ceiling; to the left
      // of that is an opening onto the hung speaker, and below 3.2 the mezzanine
      // runs across. So the screen is up and a little right of the decks, 16:9,
      // in the eyeline of the dance floor looking at the DJ.
      { pos: [0.4113, 2.437, -1.7629], size: [1.7525, 0.9858], rotY: 0 },
    ],
    // Where the DJ stands: behind the CDJs (model x 29.5, z −2.0 — the decks sit
    // at z −1.75..−1.35 and the booth's back panel at z −2.3), facing the room.
    // The performer of a set here stands on it (components/VenueStage, 2026-10-30).
    booth: { pos: [-0.7935, 0, -2.305], rotY: 0 },
    // STORY MARKS (City Stories inside scenes): on the dance floor between the
    // spawn and the booth, turned a little toward each other, facing the way you
    // come in. Same convention as the Corner Market's: rotY 0 faces +z.
    storyMarks: {
      in0: { pos: [-0.35, 0, 0.6], rotY: 0.4 },
      in1: { pos: [0.35, 0, 0.6], rotY: -0.4 },
    },
  },

  // THE COFFEE SHOP, bespoke: The Jahva House (Khari, 2026-10-30: "a loft style
  // coffee shop with a barista and tables you can sit at. near the back
  // (upstairs preferably) is a small listening area where spoken word
  // performances will happen"). Built for it: ROOMS.jahva (scripts/make_jahva.mjs),
  // which carries everything the room needs — its tables, cup spots and serve
  // marks (CafeCounter), the pickup end of the bar, the barista's spot, the
  // spoken-word corner upstairs (`stage.spoken`), the loft (`multiLevel`). No
  // wall screens: the barista is the resident, and the room is for words.
  'coffee-shop': {
    room: 'jahva',
    duplicable: false,
    // SITED like HQ (2026-10-31): the tall windows show the live street.
    sited: true,
  },
};

const HEX = /^#[0-9a-f]{3,8}$/i;
const clamp = (v, a, b) => Math.min(b, Math.max(a, v));

/**
 * The room an instance is built as: its type's room with the instance's own
 * name, look and host. `base` is the ROOMS entry the type names. Never carries
 * the base room's hand pins (`anchorMesh`) — an instance stands on its own site.
 */
export function instanceRoom(typeKey, type, base, venue) {
  const { anchorMesh, ...rest } = base; // eslint-disable-line no-unused-vars
  const light = Number(venue.light);
  // A type's marks may face a target (`face`, city/facing) like a room's.
  return faceMarks({
    ...rest,
    name: venue.name,
    accent: HEX.test(venue.accent || '') ? venue.accent : (base.accent || '#ffb25e'),
    light: Number.isFinite(light) && light > 0 ? clamp(light, 0.05, 2) : type.light ?? base.light,
    venueType: typeKey,
    venueBase: type.room,
    // Saved settings are per room id, and a new instance has none: it reads its
    // base room's row (fit, spawn, height) until somebody tunes this one.
    screens: type.screens || null,
    screensFitTo: type.screensFitTo || base.fitTo || null,
    booth: type.booth || null,
    // The base room's owner's sign belongs to the base room (rooms.js warehouse), not to every club.
    hostSign: type.hostSign || null,
    // A SITED type stands in its building (MeshInterior at the venue's anchor,
    // turned with its site; the city stays drawn around it, its own building
    // stepping aside — CityModel). The base room itself stays virtual: it has no
    // building of its own to stand in.
    ...(type.sited ? { placement: 'sited' } : {}),
    storyMarks: type.storyMarks || base.storyMarks,
    hostMedia: venue.host?.media || [],
    registry: true,
  }, venue.slug || typeKey);
}
